import { journeyCommand } from './journeyOperations';
import type { CleanupTable, JourneyCommand, JourneyIntent } from './journeyOperations';

export function pendingJourneyKey(actor: string, intents: readonly JourneyIntent[]): string {
  return (
    'tap-journey-pending:' +
    JSON.stringify([
      actor,
      intents.map((intent) =>
        intent.kind === 'merge'
          ? ['merge', intent.keeperId, intent.loserId]
          : ['cleanup', intent.table, intent.id],
      ),
    ])
  );
}

/** A retained receipt can represent a committed action with a lost response.
 * A newly inactive screen must not turn that uncertainty into legacy writes,
 * including after its original drawer was closed. Storage failure also refuses. */
export function assertNoPendingLegacyJourney(actor: string) {
  if (listPendingJourneys(actor).length > 0)
    throw new Error('Resolve unfinished actions before starting legacy removal or merge.');
}

/** Browser-session retry identity, not an authorization cache. Closing or
 * refreshing a dialog must not turn a lost response into a new operation.
 * No credentials are stored. Every retry still checks current database access. */
export function readPendingJourney(key: string, intents: readonly JourneyIntent[]) {
  const raw = sessionStorage.getItem(key);
  if (raw === null) return null;
  const parsed: unknown = JSON.parse(raw);
  if (parsed === null || typeof parsed !== 'object') throw new Error('journey_pending_invalid');
  const value = parsed as Record<string, unknown>;
  if (
    !Array.isArray(value.commands) ||
    value.commands.length !== intents.length ||
    !Number.isInteger(value.completed) ||
    (value.completed as number) < 0 ||
    (value.completed as number) > intents.length
  )
    throw new Error('journey_pending_invalid');
  const commands = intents.map((intent, index) => {
    const command: unknown = (value.commands as unknown[])[index];
    if (command === null || typeof command !== 'object') throw new Error('journey_pending_invalid');
    const fields = command as Record<string, unknown>;
    if (
      typeof fields.reason !== 'string' ||
      typeof fields.requestId !== 'string' ||
      typeof fields.actorId !== 'string'
    )
      throw new Error('journey_pending_invalid');
    return journeyCommand(intent, fields.reason, fields.requestId, fields.actorId);
  });
  return { commands, completed: value.completed as number };
}

export function savePendingJourney(
  key: string,
  commands: readonly JourneyCommand[],
  completed: number,
) {
  sessionStorage.setItem(key, JSON.stringify({ commands, completed }));
  window.dispatchEvent(new Event('tap-journey-pending-changed'));
}

export function clearPendingJourney(key: string) {
  sessionStorage.removeItem(key);
  window.dispatchEvent(new Event('tap-journey-pending-changed'));
}

/** Recovery is actor-scoped, not record-scoped. A committed merge can remove
 * the very row that opened the dialog, or shrink a duplicate group. The toolbar
 * reads the frozen original intents so neither change makes its retry vanish. */
export function listPendingJourneys(actor: string) {
  const pending: { key: string; intents: JourneyIntent[] }[] = [];
  for (let index = 0; index < sessionStorage.length; index += 1) {
    const key = sessionStorage.key(index);
    if (key === null || !key.startsWith('tap-journey-pending:')) continue;
    const scope: unknown = JSON.parse(key.slice('tap-journey-pending:'.length));
    if (!Array.isArray(scope) || scope[0] !== actor) continue;
    const raw = sessionStorage.getItem(key);
    const value: unknown = JSON.parse(raw ?? 'null');
    if (value === null || typeof value !== 'object') throw new Error('journey_pending_invalid');
    const fields = value as Record<string, unknown>;
    if (!Array.isArray(fields.commands)) throw new Error('journey_pending_invalid');
    const intents = fields.commands.map((item: unknown): JourneyIntent => {
      if (item === null || typeof item !== 'object') throw new Error('journey_pending_invalid');
      const command = item as Record<string, unknown>;
      if (typeof command.label !== 'string') throw new Error('journey_pending_invalid');
      if (
        command.kind === 'merge' &&
        typeof command.keeperId === 'string' &&
        typeof command.loserId === 'string'
      )
        return {
          kind: 'merge',
          keeperId: command.keeperId,
          loserId: command.loserId,
          label: command.label,
        };
      if (
        command.kind === 'cleanup' &&
        typeof command.id === 'string' &&
        typeof command.table === 'string' &&
        ['leads', 'prospects', 'lead_lists', 'lead_list_memberships', 'copy_sets'].includes(
          command.table,
        )
      )
        return {
          kind: 'cleanup',
          id: command.id,
          table: command.table as CleanupTable,
          label: command.label,
        };
      throw new Error('journey_pending_invalid');
    });
    if (!intents.length || pendingJourneyKey(actor, intents) !== key)
      throw new Error('journey_pending_invalid');
    readPendingJourney(key, intents);
    if (
      fields.commands.some(
        (item: unknown) =>
          item === null ||
          typeof item !== 'object' ||
          (item as Record<string, unknown>).actorId !== actor,
      )
    )
      throw new Error('journey_pending_invalid');
    pending.push({ key, intents });
  }
  return pending;
}
